// 🚨 MAINTENANCE MANUELLE - Changez à `true` pour activer immédiatement la maintenance
//****a noter faux aussi changer dans use maintenanceDegub.ts le setForceShow a true pour le forcer en dev****
const FORCE_MAINTENANCE = false; // ✅ Activé temporairement pour test

import { supabase } from './supabase';
import { useEmbedStore } from './embedStore';
import { isIOSDevice } from './utils/isIOS';

/**
 * Vérifie si la maintenance est forcée manuellement (fallback local)
 */
export const isMaintenanceForced = (): boolean => {
  return FORCE_MAINTENANCE;
};

/**
 * Ancienne logique horaire (garde pour compatibilité)
 */
export const isMaintenanceTime = (): boolean => {
  if (FORCE_MAINTENANCE) return true;
  const now = new Date();
  const montrealTime = new Date(now.toLocaleString('en-US', { timeZone: 'America/Montreal' }));
  const hours = montrealTime.getHours();
  return hours >= 0 && hours < 5;
};

/**
 * Interroge la base pour savoir si la maintenance est activée.
 * Retourne `true` si la clé `maintenance_mode` existe et que `value->>'enabled' = 'true'`.
 * En cas d'erreur, retombe sur `FORCE_MAINTENANCE`.
 */
export const isMaintenanceEnabled = async (): Promise<boolean> => {
  try {
    const { data, error } = await supabase
      .from('app_settings')
      .select('value')
      .eq('key', 'maintenance_mode')
      .single();

    if (error) {
      // Si l'erreur est PGRST116 (no rows), considérer comme désactivé
      if ((error as any).code === 'PGRST116') return false;
      console.warn('Erreur lors du check maintenance en DB, fallback:', error);
      return FORCE_MAINTENANCE;
    }

    const enabled = Boolean(data?.value?.enabled === true || data?.value?.enabled === 'true');
    return enabled;
  } catch (err) {
    console.warn('Erreur inattendue isMaintenanceEnabled():', err);
    return FORCE_MAINTENANCE;
  }
};

/**
 * S'abonne aux changements de la table app_settings et appelle callback(enabled: boolean)
 * Retourne une fonction de cleanup pour annuler la subscription.
 *
 * REST reste la source de vérité (isMaintenanceEnabled()) : Realtime n'est qu'une amélioration
 * facultative pour rafraîchir l'état sans recharger. Une défaillance du WebSocket ne doit donc
 * jamais bloquer l'app ni remonter comme erreur applicative.
 *
 * Sur iOS en mode EMBED (Arsenal ou TSLH), Safari peut jeter une exception synchrone à l'ouverture
 * du WebSocket dans l'iframe cross-origin (ITP intermittente) : on n'ouvre alors aucun channel.
 */
export const subscribeToMaintenanceMode = (callback: (enabled: boolean) => void) => {
  if (useEmbedStore.getState().isEmbed && isIOSDevice()) {
    return () => {};
  }

  try {
    const subscription = supabase
      .channel('public:app_settings')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'app_settings' }, (payload) => {
        try {
          const newRow = (payload as any).new;
          if (!newRow) return;
          if (newRow.key === 'maintenance_mode') {
            const enabled = Boolean(newRow.value?.enabled === true || newRow.value?.enabled === 'true');
            callback(enabled);
          }
        } catch (payloadErr) {
          console.warn('subscribeToMaintenanceMode: payload ignoré', payloadErr);
        }
      })
      .subscribe((status) => {
        // CHANNEL_ERROR/TIMED_OUT sont attendus (réseau, ITP iOS) : jamais fatals, jamais de console.error.
        if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
          console.debug('subscribeToMaintenanceMode: statut non critique', status);
        }
      });

    return () => {
      try {
        supabase.removeChannel(subscription);
      } catch (e) {
        // Older supabase client fallback
        try { (subscription as any).unsubscribe(); } catch (e2) { /* ignore */ }
      }
    };
  } catch (e) {
    console.warn('subscribeToMaintenanceMode failed', e);
    return () => {};
  }
};

/**
 * Retourne l'heure actuelle de Montréal formatée
 */
export const getMontrealTime = (): string => {
  const now = new Date();
  return now.toLocaleString("fr-CA", { 
    timeZone: "America/Montreal",
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  });
};

/**
 * Retourne le temps restant avant la fin de la maintenance (5h AM Montréal)
 * Si la maintenance est forcée manuellement, affiche un message différent
 */
export const getTimeUntilMaintenanceEnd = (): string => {
  // Si la maintenance est forcée manuellement, ne pas afficher de temps restant
  if (FORCE_MAINTENANCE) {
    return "Maintenance en cours";
  }

  const now = new Date();
  const montrealTime = new Date(now.toLocaleString("en-US", { timeZone: "America/Montreal" }));
  
  // Créer une date pour 5h AM du même jour
  const endTime = new Date(montrealTime);
  endTime.setHours(5, 0, 0, 0);
  
  // Si on est déjà passé 5h, c'est pour le lendemain
  if (montrealTime.getHours() >= 15) {
    endTime.setDate(endTime.getDate() + 1);
  }
  
  const diff = endTime.getTime() - montrealTime.getTime();
  const hours = Math.floor(diff / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
  
  if (hours > 0) {
    return `${hours}h ${minutes}min`;
  } else {
    return `${minutes}min`;
  }
};

/**
 * Liste des routes accessibles pendant la maintenance
 */
const ALLOWED_ROUTES_DURING_MAINTENANCE = [
  '/auth',
  '/league-selection',
  '/select-league',
  '/dashboard',
  '/my-office',
  '/standings'
];

/**
 * Vérifie si une route est autorisée pendant la maintenance
 */
export const isRouteAllowedDuringMaintenance = (pathname: string): boolean => {
  return ALLOWED_ROUTES_DURING_MAINTENANCE.some(route => pathname.startsWith(route));
};

/**
 * Vérifie si on doit afficher le modal de maintenance
 * (maintenance active ET route non autorisée)
 */
export const shouldShowMaintenanceModal = (pathname: string): boolean => {
  return isMaintenanceTime() && !isRouteAllowedDuringMaintenance(pathname);
};
